<script setup lang="ts">
import { computed, ref, useAttrs, watch } from 'vue';
import type { DateTimePickerProps } from '../types';
import DatePicker from '@/components/forms/DatePicker/DatePicker.vue';
import TimePicker from '@/components/forms/TimePicker/TimePicker.vue';
import { DateTime } from 'luxon';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<DateTimePickerProps>(), {
  mode: 'edit',
});

const emits = defineEmits<{
  'update:modelValue': [dateTime: DateTime | undefined];
}>();

const passdownProps = computed(() => ({
  ...props,
  label: undefined,
  modelValue: undefined,
  errorMessage: undefined,
}));

const attrs = useAttrs();
const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

const timeModel = ref<DateTime>(props.modelValue || DateTime.now());
const dateModel = ref<DateTime>(props.modelValue || DateTime.now());

// Track which half the user cleared so a shared error only skips a filled half.
const dateFilled = ref(!!props.modelValue?.isValid);
const timeFilled = ref(!!props.modelValue?.isValid);

watch(
  () => props.modelValue,
  (modelValue) => {
    if (modelValue?.isValid) {
      timeModel.value = modelValue;
      dateModel.value = modelValue;
      dateFilled.value = true;
      timeFilled.value = true;
    }
  },
);

const fieldErrors = computed(() => {
  const errorMessage = props.errorMessage;
  if (!errorMessage) return {};

  return {
    // Hide only when this half is filled and the other is empty (#41749).
    date: dateFilled.value && !timeFilled.value ? undefined : errorMessage,
    time: timeFilled.value && !dateFilled.value ? undefined : errorMessage,
  };
});

function updateDate(dateTime: DateTime | undefined) {
  dateFilled.value = !!dateTime?.isValid;
  emits('update:modelValue', dateTime);
}

function updateTime(dateTime: DateTime | undefined) {
  timeFilled.value = !!dateTime?.isValid;
  emits('update:modelValue', dateTime);
}
</script>

<template>
  <div :class="$attrs.class">
    <v-label v-if="label">{{ label }}</v-label>
    <p v-if="mode === 'details'">
      <DateTimeFormatter :value="modelValue" :format="formatForDetails" />
    </p>

    <v-row v-else>
      <v-col>
        <DatePicker
          v-bind="{ ...passdownProps, ...passdownAttrs }"
          :modelValue="dateModel"
          :errorMessage="fieldErrors.date"
          :data-testid="`${label}_Date`"
          @update:modelValue="updateDate"
        />
      </v-col>
      <v-col>
        <TimePicker
          v-bind="{ ...passdownProps, ...passdownAttrs }"
          :modelValue="timeModel"
          :errorMessage="fieldErrors.time"
          :disabled="props.timeDisabled"
          :data-testid="`${label}_Time`"
          @update:modelValue="updateTime"
        />
      </v-col>
    </v-row>
  </div>
</template>
